import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { endpoints, POST_LOGIN_REDIRECT_KEY, postLoginDestination } from '../services/api';
import AuthShell from '../components/AuthShell';
import { getFriendlyError } from '../components/errorUtils';
import ReactGA from 'react-ga4';


const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const redirectUrl = postLoginDestination(searchParams.get('redirect'));
  const passwordReset = searchParams.get('reset') === 'success';
  const guideName = searchParams.get('guide');   // set by SharePage — names the guide the link is for

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    try {
      const response = await fetch(endpoints.auth.token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to login');
      }

      const data = await response.json();
      login(data.access_token);
      ReactGA.event({ category: 'User', action: 'login', label: 'email' });
      window.postMessage(
        { type: 'NEXAURA_AUTH_TOKEN', token: data.access_token },
        window.location.origin
      );
      navigate(redirectUrl);

    } catch (err) {
      setError(getFriendlyError(err.message, 'Login Page'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    localStorage.setItem(POST_LOGIN_REDIRECT_KEY, redirectUrl);
    window.location.href = endpoints.auth.google;
  };

  return (
    <AuthShell display="Welcome back." say="Your guides are right where you left them.">
        <h1 className="auth-title">Log in to Michii</h1>

        {guideName && (
          <p className="auth-success">Log in to get access to “{guideName}”</p>
        )}

        {passwordReset && (
          <p className="auth-success">Password updated. Log in with your new password.</p>
        )}

        {/* ── Google ── */}
        <button onClick={handleGoogleLogin} className="google-signin-button">
          <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.96 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
          Continue with Google
        </button>

        {/* ── Divider ── */}
        <div className="auth-divider">
          <span>or</span>
        </div>

        {/* ── Email / Password ── */}
        <form onSubmit={handleEmailLogin} className="auth-form">
          {error && <p className="auth-error">{error}</p>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="auth-forgot">
            <Link to="/forgot-password">Forgot password?</Link>
          </div>

          <button
            type="submit"
            className="cta-button primary auth-button"
            disabled={loading}
          >
            {loading ? 'Logging in…' : 'Log in'}
          </button>
        </form>

        <p className="auth-switch">
          Don't have an account? <Link to={`/register${window.location.search}`}>Create one</Link>
        </p>
    </AuthShell>
  );
};

export default LoginPage;